import { Box, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SessionManager } from '@src/utils/sessionManager';
interface CountdownTimerProps {
  locationId: string;
  onExpire?: () => void;
  onClick?: () => void;
  slotInfo?: {
    date?: string;
    time?: string;
    provider?: string;
    location?: string;
  };
}
export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  locationId,
  onExpire,
  onClick,
  slotInfo
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);

  // Format seconds as "m:ss"
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    return `${minutes}:${remainingSecs < 10 ? '0' : ''}${remainingSecs}`;
  };

  // 7 minutes = 420 seconds (total hold time)

  useEffect(() => {
    // Check if there's an active hold for this location
    const isHoldActive = SessionManager.hasActiveHold(locationId);
    setIsActive(isHoldActive);
    if (isHoldActive) {
      // Get initial remaining seconds
      const initialSeconds = SessionManager.getHoldRemainingSeconds(locationId);
      setRemainingSeconds(initialSeconds);

      // Set up interval to update the countdown.
      // Always derive time from the stored expiration timestamp so the timer
      // stays accurate even when the browser throttles background tabs.
      const intervalId = setInterval(() => {
        const remaining = SessionManager.getHoldRemainingSeconds(locationId);
        if (remaining <= 0) {
          clearInterval(intervalId);
          setIsActive(false);
          setRemainingSeconds(0);
          // Release the hold when expired
          SessionManager.releaseHold(locationId);
          // Call onExpire callback if provided
          if (onExpire) onExpire();
        } else {
          setRemainingSeconds(remaining);
        }
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [locationId, onExpire]);
  if (!isActive) return null;

  // Create tooltip content with slot information
  const tooltipContent = slotInfo ? <Box sx={{
    p: 1
  }}>
			{slotInfo.date && <Typography>
					<strong>Date:</strong> {slotInfo.date}
				</Typography>}
			{slotInfo.time && <Typography>
					<strong>Time:</strong> {slotInfo.time}
				</Typography>}
			{slotInfo.provider && <Typography>
					<strong>Provider:</strong> {slotInfo.provider}
				</Typography>}
			{slotInfo.location && <Typography>
					<strong>Location:</strong> {slotInfo.location}
				</Typography>}
		</Box> : 'Reserved Visit Slot';
  return <Tooltip title={tooltipContent} arrow placement="bottom" data-sentry-element="Tooltip" data-sentry-component="CountdownTimer" data-sentry-source-file="CountdownTimer.tsx">
			<Box onClick={onClick} sx={{
      display: 'flex',
      width: '100%',
      maxWidth: '315px',
      padding: '15px 20px',
      backgroundColor: '#EBF1FF',
      borderRadius: '8px',
      border: '1px solid #C6D4F2',
      mt: '20px',
      mb: '32px',
      justifyContent: 'center',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#D9E5FF',
        transition: 'background-color 0.3s'
      }
    }} data-sentry-element="Box" data-sentry-source-file="CountdownTimer.tsx">
				<Typography sx={{
        color: '#013592',
        fontFamily: 'Avenir',
        fontSize: '14px',
        fontWeight: 350,
        lineHeight: '20px'
      }} data-sentry-element="Typography" data-sentry-source-file="CountdownTimer.tsx">
					We&apos;re holding this slot for you for{' '}
					<span style={{
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '22px'
        }}>
						{formatTime(remainingSeconds)} minutes
					</span>
				</Typography>
			</Box>
		</Tooltip>;
};
export default CountdownTimer;